/* eslint-disable no-unused-vars */
import React, { useState, useRef } from "react";
import { X, Upload, FileText, AlertCircle, CheckCircle } from "lucide-react";
import { useGrowth } from "../../../context/GrowthContext";

const CSV_HEADERS = ["name", "eventName", "eventType", "email", "instagram", "website", "city", "estimatedVendorCount", "contactForm", "notes"];

function parseCSV(text) {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase().replace(/\s+/g, ""));
  return lines.slice(1).map((line) => {
    const vals = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
    const row = {};
    headers.forEach((h, i) => {
      const mapped = {
        name: "name", organizer: "name", organizername: "name",
        eventname: "eventName", event: "eventName", event_name: "eventName",
        eventtype: "eventType", type: "eventType", event_type: "eventType",
        email: "email",
        instagram: "instagram", ig: "instagram",
        website: "website", url: "website", site: "website",
        city: "city", location: "city",
        vendorcount: "estimatedVendorCount", vendors: "estimatedVendorCount", estimatedvendorcount: "estimatedVendorCount",
        contactform: "contactForm", form: "contactForm",
        notes: "notes",
      }[h] || h;
      if (vals[i]) row[mapped] = vals[i];
    });
    return row;
  }).filter((r) => r.name || r.eventName);
}

export default function ImportModal({ onClose }) {
  const { bulkImport } = useGrowth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const rows = parseCSV(e.target.result);
        setPreview(rows);
        setError("");
      } catch {
        setError("Couldn't parse that CSV. Check the format.");
      }
    };
    reader.readAsText(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const handleImport = async () => {
    if (!preview.length) return;
    setImporting(true);
    try {
      await bulkImport(preview);
      setDone(true);
    } catch {
      setError("Import failed. Try again.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Import Leads</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {done ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="font-semibold text-gray-900">{preview.length} leads imported</p>
              <p className="text-sm text-gray-500 mt-1">Ready to research and reach out.</p>
              <button onClick={onClose} className="mt-5 px-5 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800">
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-gray-400 transition"
              >
                <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700">Drop a CSV or click to upload</p>
                <p className="text-xs text-gray-400 mt-1">Columns: name, eventName, email, instagram, website, city, eventType, estimatedVendorCount</p>
              </div>

              {/* Template download hint */}
              <p className="text-xs text-gray-400 text-center">
                Headers are flexible — we'll auto-map common column names.
              </p>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}

              {/* Preview */}
              {preview.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">{preview.length} leads ready to import</p>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
                    {preview.slice(0, 10).map((row, i) => (
                      <div key={i} className="px-4 py-2.5 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{row.name || "—"}</p>
                          <p className="text-xs text-gray-400">{row.eventName} · {row.city}</p>
                        </div>
                        {row.email && <span className="text-xs text-gray-400">{row.email}</span>}
                      </div>
                    ))}
                    {preview.length > 10 && (
                      <p className="text-xs text-gray-400 px-4 py-2">+ {preview.length - 10} more</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {!done && (
          <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
            <button
              onClick={handleImport}
              disabled={!preview.length || importing}
              className="px-5 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-40 transition"
            >
              {importing ? "Importing..." : `Import ${preview.length} Leads`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
